import { useState, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { METERS_PER_MILE } from '@/config/constants';
import { useShopSettings } from '@/context/ShopSettingsContext';
import { isDesktopOffline } from '@/api/desktopSync';
import { googleRouteRequest, readGoogleRoute, routeMetric as metric, ROUTE_WAIT_MS } from '@/utils/googleRoutes';
import { readOrderCoordinate } from '@/utils/mapCoordinates';
import { selectShopRouteOrigin } from '@/utils/shopRouteOrigin';

/**
 * Parse maneuver type to a display-friendly icon
 */
const getManeuverIcon = (maneuver) => {
  const icons = {
    'turn-left': '↰',
    'turn-right': '↱',
    'turn-slight-left': '↖',
    'turn-slight-right': '↗',
    'turn-sharp-left': '⮢',
    'turn-sharp-right': '⮣',
    'uturn-left': '⮌',
    'uturn-right': '⮎',
    'straight': '↑',
    'keep-left': '↖',
    'keep-right': '↗',
    'merge': '⤨',
    'ramp-left': '↰',
    'ramp-right': '↱',
    'fork-left': '↖',
    'fork-right': '↗',
    'roundabout-left': '↺',
    'roundabout-right': '↻',
  };
  return icons[maneuver] || '↑';
};

/**
 * Format distance - use feet if under 0.1 miles
 */
const formatDistance = (meters) => {
  const miles = meters / METERS_PER_MILE;
  if (miles < 0.1) {
    const feet = Math.round(meters * 3.28084);
    return `${feet} ft`;
  }
  return `${miles.toFixed(1)} mi`;
};

/**
 * Strip HTML tags from instruction text
 */
const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Keep the original stop/order alongside the normalized point for labels and
// callbacks. Missing points never alter run membership or reorder valid stops.
const usableStops = run => (run?.populated_stops || []).flatMap(stop => {
  const coordinate = readOrderCoordinate(stop.order);
  return coordinate ? [{ stop, coordinate }] : [];
});
const routeFacts = (run, origin) => JSON.stringify([origin, usableStops(run).map(
  ({ stop, coordinate }) => [stop.order_id || stop.order.id, coordinate.latitude, coordinate.longitude],
)]);
const describeResult = (result, count) => {
  const { route, routeInfo } = readGoogleRoute(result, count);
  const legs = route.legs;
  const steps = legs.map((leg, legIndex) => {
    const meters = metric(leg.distanceMeters);
    const seconds = metric(leg.durationMillis) / 1000;
    if (!Array.isArray(leg.steps)) throw new Error('Invalid directions steps');
    return {
      legIndex, stopSequence: legIndex + 1,
      legDistance: formatDistance(meters), legDuration: Math.round(seconds / 60),
      steps: leg.steps.map((step, stepIndex) => ({
        id: `${legIndex}-${stepIndex}`,
        instruction: stripHtml(typeof step.instructions === 'string' ? step.instructions : ''),
        distance: formatDistance(metric(step.distanceMeters)),
        distanceValue: step.distanceMeters,
        maneuver: normalizeManeuver(step.maneuver), icon: getManeuverIcon(normalizeManeuver(step.maneuver)),
      })),
    };
  });
  if (!Array.isArray(route.path) || !route.path.length || typeof route.createPolylines !== 'function') {
    throw new Error('Invalid route geometry');
  }
  const warnings = route.warnings || [];
  if (!Array.isArray(warnings) || warnings.some(item => typeof item !== 'string')) throw new Error('Invalid route warnings');
  return { steps, routeInfo, warnings: warnings.map(stripHtml) };
};

const normalizeManeuver = value => typeof value === 'string' ? value.toLowerCase().replaceAll('_', '-') : 'straight';
export { ROUTE_WAIT_MS };
const failureMessage = 'Directions are unavailable. Your run and orders have not changed.';

/** Current dispatch display only: no persisted route or business-command retry. */
const useRouting = (map, routeColor = null, context = {}) => {
  const { routeOrigin } = useShopSettings();
  const origin = selectShopRouteOrigin(routeOrigin);
  const { selectedRun, captureSelection, selectedDateString, driverMode, offline } = context;
  const selection = captureSelection?.();
  const cycle = selection?.cycle;
  const selectionNumber = selection?.selection;
  const runId = selectedRun?.id;
  const facts = routeFacts(selectedRun, origin.identity);
  // A fresh object on A -> B -> A prevents an old matching route from reviving.
  const scope = useMemo(() => ({
    map, origin: origin.input, originIdentity: origin.identity, facts, runId, selectedDateString, driverMode, offline,
    selection: cycle ? { cycle, selection: selectionNumber } : null,
  // Identity encodes the normalized input; allocation/refresh alone must not refetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [map, origin.identity, facts, runId, selectedDateString, driverMode, offline, cycle, selectionNumber]);
  const latest = useRef(null);
  latest.current = { scope, context, color: routeColor || '#2563eb' };
  const active = useRef(null);
  const [view, setView] = useState(null);
  const owns = useCallback(owner => {
    const now = latest.current;
    return Boolean(owner?.active && active.current === owner && now.scope === owner.scope
      && !now.scope.offline && !isDesktopOffline()
      && now.context.isScopeCurrent?.()
      && now.context.isSelectionCurrent?.(owner.scope.selection)
      && routeFacts(now.context.getRun?.(owner.scope.runId), now.scope.originIdentity) === owner.scope.facts);
  }, []);
  const detach = useCallback(owner => {
    if (!owner) return;
    clearTimeout(owner.timer);
    const lines = owner.lines;
    owner.lines = [];
    for (const line of lines) {
      try { line.setMap(null); } catch { /* still detach other owned lines */ }
    }
  }, []);
  const clearRoute = useCallback(() => {
    const owner = active.current;
    if (owner) { owner.active = false; detach(owner); }
    active.current = null;
    setView(null);
  }, [detach]);

  useLayoutEffect(() => {
    const owner = { scope, active: true, lines: [], attempt: 0, phase: 'idle' };
    active.current = owner;
    const publish = data => { if (owns(owner)) setView({ owner, ...data }); };
    const fail = (message = failureMessage) => {
      if (!owns(owner)) return;
      owner.phase = 'error';
      detach(owner);
      publish({ status: 'error', error: message });
    };
    owner.fail = fail;
    const start = () => {
      if (!owns(owner) || owner.phase === 'loading' || !scope.map || !scope.runId || !scope.origin) return;
      const stops = usableStops(latest.current.context.getRun(scope.runId));
      if (!stops.length) return;
      const attempt = ++owner.attempt;
      owner.phase = 'loading';
      publish({ status: 'loading' });
      const current = () => owns(owner) && owner.attempt === attempt && owner.phase === 'loading';
      // This only bounds UI waiting. Google work is not cancelled; late results
      // cannot publish, and only an explicit retry admits another attempt.
      owner.timer = setTimeout(() => {
        if (current()) fail('Directions took too long. Your run and orders have not changed.');
      }, ROUTE_WAIT_MS);
      try {
        window.google.maps.routes.Route.computeRoutes(googleRouteRequest(scope.origin, stops.map(item => item.coordinate), true)).then(result => {
          if (!current()) return;
          try {
            const display = describeResult(result, stops.length);
            owner.lines = result.routes[0].createPolylines({ polylineOptions: {
              strokeColor: latest.current.color, strokeWeight: 4, strokeOpacity: 0.8,
            } });
            if (!Array.isArray(owner.lines)) { owner.lines = []; throw new Error('Invalid route lines'); }
            if (!owner.lines.length) throw new Error('Missing route lines');
            owner.lines.forEach(line => line.setMap(scope.map));
            clearTimeout(owner.timer);
            owner.phase = 'ready';
            publish({ status: 'ready', ...display });
          } catch { fail(); }
        }).catch(() => { if (current()) fail(); });
      } catch { fail(); }
    };
    owner.retry = () => {
      if (owns(owner) && owner.phase === 'error') start();
    };
    setView(null);
    // StrictMode's retired first setup must not issue a provider request.
    Promise.resolve().then(start);
    return () => {
      owner.active = false;
      detach(owner);
      if (active.current === owner) active.current = null;
    };
  }, [scope, owns, detach]);

  useLayoutEffect(() => {
    const owner = active.current;
    if (!owns(owner) || !owner.lines.length) return;
    try {
      owner.lines.forEach(line => line.setOptions({
        strokeColor: routeColor || '#2563eb', strokeWeight: 4, strokeOpacity: 0.8,
      }));
    } catch { owner.fail(); }
  }, [routeColor, owns]);

  const visible = view && owns(view.owner) ? view : null;
  const stops = usableStops(selectedRun);
  const needsOrigin = !offline && stops.length > 0 && Boolean(selectedRun);
  return {
    routeInfo: visible?.routeInfo || null,
    routeWarnings: visible?.warnings || [],
    // Labels are current display data, not a reason to ask Google again.
    directionSteps: (visible?.steps || []).map((leg, i) => ({ ...leg, order: stops[i]?.stop.order || null })),
    loadingRoute: visible?.status === 'loading' || (needsOrigin && origin.state === 'loading'),
    routeError: needsOrigin && origin.state === 'unavailable'
      ? { message: origin.error || 'Shop route origin is unavailable. Ask an administrator to update Shop Profile.', retryable: false }
      : visible?.error || (needsOrigin && origin.stale
        ? { message: 'Using the last saved shop location; the latest settings check failed.', retryable: false } : null),
    retryRoute: () => { if (visible?.status === 'error') visible.owner.retry(); },
    clearRoute,
  };
};

export default useRouting;
